import { useEffect, useState } from 'react';
import { Tabs, Table, Tag, Button, Input, Space, Popconfirm, Card, App, Typography, Select, Alert } from 'antd';
import { SearchOutlined, DisconnectOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import {
  AuditLog,
  AuditOutcome,
  AuditSource,
  FinAPIConnection,
  FinAPIConnectionStatus,
  SavingsPlanStaffListItem,
  SavingsPlanStatus,
} from '@mygate/common';
import { finapiStaffService } from '../services/finapi';

const STATUS_COLORS: Record<FinAPIConnectionStatus, string> = {
  [FinAPIConnectionStatus.PENDING]: 'gold',
  [FinAPIConnectionStatus.ACTIVE]: 'green',
  [FinAPIConnectionStatus.UPDATE_REQUIRED]: 'orange',
  [FinAPIConnectionStatus.EXPIRED]: 'red',
  [FinAPIConnectionStatus.FAILED]: 'red',
  [FinAPIConnectionStatus.REVOKED]: 'default',
};

const SOURCE_COLORS: Record<AuditSource, string> = {
  [AuditSource.USER]: 'blue',
  [AuditSource.STAFF]: 'purple',
  [AuditSource.SYSTEM]: 'default',
  [AuditSource.WEBHOOK]: 'cyan',
};

const SPARPLAN_STATUS_COLORS: Record<SavingsPlanStatus, string> = {
  [SavingsPlanStatus.DRAFT]: 'default',
  [SavingsPlanStatus.AWAITING_SETUP]: 'gold',
  [SavingsPlanStatus.ACTIVE]: 'green',
  [SavingsPlanStatus.FAILED]: 'red',
  [SavingsPlanStatus.CANCELLED]: 'default',
};

/**
 * Backoffice oversight page for the finAPI integration. Two tabs:
 *   - Connections: filter by user, status; revoke privileged action.
 *   - Audit log: browse `finapi.*` audit entries with filters.
 *
 * All API calls are routed through `finapiStaffService`. The Ant Design
 * `App.useApp()` hook is mandatory here per project convention so message
 * popups render correctly (see CLAUDE.md backoffice section).
 */
export default function FinAPIOversight() {
  const { message } = App.useApp();
  const [activeTab, setActiveTab] = useState<'connections' | 'audit' | 'sparplans'>('connections');
  const [loadErrors, setLoadErrors] = useState<{ connections?: string; sparplans?: string; audit?: string }>({});
  const [connections, setConnections] = useState<FinAPIConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(false);
  const [connectionsFilter, setConnectionsFilter] = useState<{ userId?: string; status?: string }>({});

  const [auditEntries, setAuditEntries] = useState<AuditLog[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditFilter, setAuditFilter] = useState<{
    userId?: string;
    action?: string;
    outcome?: string;
  }>({});

  const [sparplans, setSparplans] = useState<SavingsPlanStaffListItem[]>([]);
  const [sparplansLoading, setSparplansLoading] = useState(false);
  const [sparplansFilter, setSparplansFilter] = useState<{ userId?: string; status?: string }>({});

  const loadConnections = async (filters = connectionsFilter): Promise<void> => {
    setConnectionsLoading(true);
    setLoadErrors(prev => ({ ...prev, connections: undefined }));
    try {
      const response = await finapiStaffService.listConnections({ ...filters, limit: 100 });
      setConnections(response.data?.items ?? []);
    } catch (err) {
      setLoadErrors(prev => ({
        ...prev,
        connections: err instanceof Error ? err.message : 'Failed to load connections',
      }));
    } finally {
      setConnectionsLoading(false);
    }
  };

  const loadAuditLog = async (filters = auditFilter): Promise<void> => {
    setAuditLoading(true);
    setLoadErrors(prev => ({ ...prev, audit: undefined }));
    try {
      const response = await finapiStaffService.listAuditLog({ ...filters, limit: 200 });
      setAuditEntries(response.data?.items ?? []);
    } catch (err) {
      setLoadErrors(prev => ({ ...prev, audit: err instanceof Error ? err.message : 'Failed to load audit log' }));
    } finally {
      setAuditLoading(false);
    }
  };

  const loadSparplans = async (filters = sparplansFilter): Promise<void> => {
    setSparplansLoading(true);
    setLoadErrors(prev => ({ ...prev, sparplans: undefined }));
    try {
      const response = await finapiStaffService.listSavingsPlans({ ...filters, limit: 100 });
      setSparplans(response.data?.items ?? []);
    } catch (err) {
      setLoadErrors(prev => ({
        ...prev,
        sparplans: err instanceof Error ? err.message : 'Failed to load savings plans',
      }));
    } finally {
      setSparplansLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'connections') void loadConnections();
    else if (activeTab === 'audit') void loadAuditLog();
    else void loadSparplans();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleForceRevoke = async (connectionId: string): Promise<void> => {
    try {
      await finapiStaffService.forceRevokeConnection(connectionId);
      message.success('Connection revoked');
      void loadConnections();
    } catch (err) {
      message.error(err instanceof Error ? err.message : 'Force-revoke failed');
    }
  };

  const connectionColumns: ColumnsType<FinAPIConnection> = [
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 200,
      render: (id: string) => (
        <Typography.Text copyable={{ text: id }} className="table-cell-muted">
          {id}
        </Typography.Text>
      ),
    },
    { title: 'Bank', dataIndex: 'bankName', key: 'bankName' },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 150,
      render: (status: FinAPIConnectionStatus) => <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>,
    },
    {
      title: 'Last successful update',
      key: 'lastSuccessfulUpdateAt',
      width: 210,
      render: (_: unknown, row) => (
        <div className="table-cell-stack">
          <span>{row.lastSuccessfulUpdateAt ? new Date(row.lastSuccessfulUpdateAt).toLocaleString('de-DE') : '—'}</span>
          <Typography.Text className="table-cell-muted">
            Erstellt: {new Date(row.createdAt).toLocaleString('de-DE')}
          </Typography.Text>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_: unknown, row: FinAPIConnection) =>
        row.status === FinAPIConnectionStatus.REVOKED ? (
          <span style={{ color: 'rgba(0,0,0,0.4)' }}>—</span>
        ) : (
          <Popconfirm
            title="Force-revoke this connection?"
            description="Revokes the PSD2 consent at the bank and marks the connection REVOKED locally."
            onConfirm={() => void handleForceRevoke(row.id)}
            okText="Revoke"
            cancelText="Cancel"
          >
            <Button danger size="small" icon={<DisconnectOutlined />}>
              Force revoke
            </Button>
          </Popconfirm>
        ),
    },
  ];

  const auditColumns: ColumnsType<AuditLog> = [
    {
      title: 'Time',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (d: Date | string) => new Date(d).toLocaleString('de-DE'),
    },
    { title: 'Action', dataIndex: 'action', key: 'action', width: 280 },
    {
      title: 'Source',
      dataIndex: 'source',
      key: 'source',
      width: 110,
      render: (s: AuditSource) => <Tag color={SOURCE_COLORS[s] ?? 'default'}>{s}</Tag>,
    },
    { title: 'Resource', dataIndex: 'resourceType', key: 'resourceType', width: 160 },
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 200,
      render: (id?: string) =>
        id ? (
          <Typography.Text copyable={{ text: id }} className="table-cell-muted">
            {id}
          </Typography.Text>
        ) : (
          '—'
        ),
    },
    {
      title: 'Outcome',
      dataIndex: 'outcome',
      key: 'outcome',
      width: 110,
      render: (o: AuditOutcome) => <Tag color={o === AuditOutcome.SUCCESS ? 'green' : 'red'}>{o}</Tag>,
    },
    {
      title: 'Error',
      dataIndex: 'errorCode',
      key: 'errorCode',
      render: (c?: string) => (c ? <code>{c}</code> : '—'),
    },
  ];

  const sparplanColumns: ColumnsType<SavingsPlanStaffListItem> = [
    {
      title: 'User',
      dataIndex: 'userId',
      key: 'userId',
      width: 200,
      render: (id: string) => (
        <Typography.Text copyable={{ text: id }} className="table-cell-muted">
          {id}
        </Typography.Text>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 140,
      render: (status: SavingsPlanStatus) => <Tag color={SPARPLAN_STATUS_COLORS[status] ?? 'default'}>{status}</Tag>,
    },
    { title: 'Asset', dataIndex: 'cryptoSymbol', key: 'cryptoSymbol', width: 90 },
    {
      title: 'Amount',
      dataIndex: 'amountCents',
      key: 'amountCents',
      align: 'right',
      width: 130,
      render: (cents: number) => `${(cents / 100).toFixed(2)} €`,
    },
    { title: 'Day of month', dataIndex: 'dayOfMonth', key: 'dayOfMonth', width: 120 },
    { title: 'Bank', dataIndex: 'bankName', key: 'bankName' },
    {
      title: 'Last execution',
      key: 'lastExecutedAt',
      width: 210,
      render: (_: unknown, row) => (
        <div className="table-cell-stack">
          <span>{row.lastExecutedAt ? new Date(row.lastExecutedAt).toLocaleString('de-DE') : '—'}</span>
          <Typography.Text className="table-cell-muted">
            Erstellt: {new Date(row.createdAt).toLocaleString('de-DE')}
          </Typography.Text>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="page-toolbar">
        <Typography.Text type="secondary">
          Bankzugänge, Sparpläne und protokollierte Bankaktionen prüfen.
        </Typography.Text>
      </div>
      <Tabs
        activeKey={activeTab}
        onChange={key => setActiveTab(key as 'connections' | 'audit' | 'sparplans')}
        items={[
          {
            key: 'connections',
            label: 'Connections',
            children: (
              <Card
                title="Bank connections"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={() => void loadConnections()}>
                    Refresh
                  </Button>
                }
              >
                <Space style={{ marginBottom: 16 }} wrap>
                  <Input
                    placeholder="User ID"
                    prefix={<SearchOutlined />}
                    value={connectionsFilter.userId ?? ''}
                    onChange={e => setConnectionsFilter(p => ({ ...p, userId: e.target.value || undefined }))}
                    style={{ width: 260 }}
                    allowClear
                    onPressEnter={() => void loadConnections()}
                  />
                  <Select
                    aria-label="Status filtern"
                    placeholder="Alle Status"
                    value={connectionsFilter.status}
                    options={Object.values(FinAPIConnectionStatus).map(value => ({ value, label: value }))}
                    onChange={value => setConnectionsFilter(p => ({ ...p, status: value }))}
                    style={{ width: 220 }}
                    allowClear
                  />
                  <Button type="primary" onClick={() => void loadConnections()}>
                    Search
                  </Button>
                </Space>
                {loadErrors.connections && (
                  <Alert type="error" showIcon message={loadErrors.connections} style={{ marginBottom: 16 }} />
                )}
                <Table
                  rowKey="id"
                  loading={connectionsLoading}
                  dataSource={connections}
                  columns={connectionColumns}
                  pagination={{ defaultPageSize: 25 }}
                  tableLayout="fixed"
                  scroll={{ x: 900 }}
                />
              </Card>
            ),
          },
          {
            key: 'sparplans',
            label: 'Sparpläne',
            children: (
              <Card
                title="Savings plans"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={() => void loadSparplans()}>
                    Refresh
                  </Button>
                }
              >
                <Space style={{ marginBottom: 16 }} wrap>
                  <Input
                    placeholder="User ID"
                    prefix={<SearchOutlined />}
                    value={sparplansFilter.userId ?? ''}
                    onChange={e => setSparplansFilter(p => ({ ...p, userId: e.target.value || undefined }))}
                    style={{ width: 260 }}
                    allowClear
                    onPressEnter={() => void loadSparplans()}
                  />
                  <Select
                    aria-label="Status filtern"
                    placeholder="Alle Status"
                    value={sparplansFilter.status}
                    options={Object.values(SavingsPlanStatus).map(value => ({ value, label: value }))}
                    onChange={value => setSparplansFilter(p => ({ ...p, status: value }))}
                    style={{ width: 220 }}
                    allowClear
                  />
                  <Button type="primary" onClick={() => void loadSparplans()}>
                    Search
                  </Button>
                </Space>
                {loadErrors.sparplans && (
                  <Alert type="error" showIcon message={loadErrors.sparplans} style={{ marginBottom: 16 }} />
                )}
                <Table
                  rowKey="id"
                  loading={sparplansLoading}
                  dataSource={sparplans}
                  columns={sparplanColumns}
                  pagination={{ defaultPageSize: 25 }}
                  tableLayout="fixed"
                  scroll={{ x: 1100 }}
                />
              </Card>
            ),
          },
          {
            key: 'audit',
            label: 'Audit log',
            children: (
              <Card
                title="finAPI audit log"
                extra={
                  <Button icon={<ReloadOutlined />} onClick={() => void loadAuditLog()}>
                    Refresh
                  </Button>
                }
              >
                <Space style={{ marginBottom: 16 }} wrap>
                  <Input
                    placeholder="User ID"
                    prefix={<SearchOutlined />}
                    value={auditFilter.userId ?? ''}
                    onChange={e => setAuditFilter(p => ({ ...p, userId: e.target.value || undefined }))}
                    style={{ width: 260 }}
                    allowClear
                    onPressEnter={() => void loadAuditLog()}
                  />
                  <Input
                    placeholder="Action prefix (e.g. finapi.payment.)"
                    value={auditFilter.action ?? ''}
                    onChange={e => setAuditFilter(p => ({ ...p, action: e.target.value || undefined }))}
                    style={{ width: 280 }}
                    allowClear
                    onPressEnter={() => void loadAuditLog()}
                  />
                  <Select
                    aria-label="Ergebnis filtern"
                    placeholder="Alle Ergebnisse"
                    value={auditFilter.outcome}
                    options={Object.values(AuditOutcome).map(value => ({ value, label: value }))}
                    onChange={value => setAuditFilter(p => ({ ...p, outcome: value }))}
                    style={{ width: 220 }}
                    allowClear
                  />
                  <Button type="primary" onClick={() => void loadAuditLog()}>
                    Search
                  </Button>
                </Space>
                {loadErrors.audit && (
                  <Alert type="error" showIcon message={loadErrors.audit} style={{ marginBottom: 16 }} />
                )}
                <Table
                  rowKey="id"
                  loading={auditLoading}
                  dataSource={auditEntries}
                  columns={auditColumns}
                  pagination={{ defaultPageSize: 50 }}
                  tableLayout="fixed"
                  scroll={{ x: 1100 }}
                />
              </Card>
            ),
          },
        ]}
      />
    </div>
  );
}
